import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Select,
  FormControl,
  Avatar,
  Switch,
  InputAdornment,
  styled,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  FileUpload as FileUploadIcon,
  Add as AddIcon,
  Search as SearchIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  InfoOutlined as InfoIcon
} from '@mui/icons-material';
import axios from 'axios';
import { formatDistanceToNow, parseISO, isValid } from 'date-fns';
import { USER_URLS } from '../config/urls';

// Utility function to format last active time
const formatLastActive = (lastActiveDate) => {
  if (!lastActiveDate) return 'Never';
  
  try {
    const date = parseISO(lastActiveDate);
    
    if (!isValid(date)) {
      return 'Invalid date';
    }

    return formatDistanceToNow(date, {
      addSuffix: true,
      includeSeconds: true
    });

  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Date error';
  }
};

// Styled components
const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  boxShadow: 'none',
  '&.MuiButton-outlined': {
    borderColor: theme.palette.divider,
    color: theme.palette.primary.main,
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: 'transparent'
    }
  },
  '&.MuiButton-contained': {
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      boxShadow: 'none'
    }
  }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: '16px 24px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&.MuiTableCell-head': {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    fontWeight: 500,
    backgroundColor: '#FFFFFF'
  }
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 32,
  height: 32,
  backgroundColor: theme.palette.grey[200],
  color: theme.palette.grey[600],
  fontSize: '0.875rem',
  fontWeight: 500
}));

const StyledSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase': {
    '&.Mui-checked': {
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.primary.main,
        opacity: 1
      }
      
    }
  },
  '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
  '& .MuiSwitch-track': {
    backgroundColor: theme.palette.grey[300],
    opacity: 1
  }
}));

const UserManagement = () => {
  // State declarations
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [lastActiveUpdate, setLastActiveUpdate] = useState(0);
 
  const [confirmDialog, setConfirmDialog] = useState({ 
    open: false, 
    userId: null, 
    action: null, 
    message: '' 
  });
  const [currentUser, setCurrentUser] = useState(null);

  // Decode JWT token function
  const decodeToken = (token) => {
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch (error) {
      return null;
    }
  };

  // Effect to fetch users and set current user
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const decoded = decodeToken(token);
        const currentUserEmail = decoded?.sub;

        const response = await axios.get(USER_URLS.getAllUsers, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setUsers(response.data);
        
        // Set current user based on email from token
        const foundUser = response.data.find(user => user.email === currentUserEmail);
        if (foundUser) {
          setCurrentUser(foundUser);
        }
      } catch (err) {
        setError(err.message);
        console.error('Error fetching users:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Effect to update last active display
  useEffect(() => {
    const interval = setInterval(() => {
      setLastActiveUpdate(prev => prev + 1);
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Filter function
  const getFilteredUsers = () => {
    return users.filter(user => {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = 
        user.email?.toLowerCase().includes(searchLower) ||
        `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchLower) ||
        user.role?.toLowerCase().includes(searchLower);

      const matchesRole = !roleFilter || user.role === roleFilter;
      
      const matchesStatus = !statusFilter || 
        (statusFilter === 'active' && user.status) ||
        (statusFilter === 'inactive' && !user.status);

      return matchesSearch && matchesRole && matchesStatus;
    });
  };

  // Action handlers
  const handleActionsClick = async (event, user) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        USER_URLS.updateUserLastActive(user.id),
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
    } catch (error) {
      console.error('Error updating last active:', error);
    }
    
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleActionsClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleStatusChange = async (userId, currentStatus) => {
    setConfirmDialog({
      open: true,
      userId,
      action: 'status',
      currentStatus,
      message: `Are you sure you want to ${currentStatus ? 'deactivate' : 'activate'} this user?`
    });
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        USER_URLS.updateUserRole(userId),
        { role: newRole },  // Match the backend's expected format
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
  
      // Update local state after successful API call
      setUsers(users.map(user => 
        user.id === userId ? { ...user, role: newRole } : user
      ));
    } catch (error) {
      console.error('Error updating role:', error);
      // Add user feedback
      alert('Failed to update user role. Please try again.');
    }
  };
  const handleConfirmDialog = async () => {
    const { userId, action, currentStatus } = confirmDialog;
    
    if (action === 'status') {
      try {
        const token = localStorage.getItem('token');
        await axios.put(
          USER_URLS.updateUserStatus(userId),
          { status: !currentStatus },
          { headers: { Authorization: `Bearer ${token}` }}
        );
        
        setUsers(users.map(user => 
          user.id === userId ? { ...user, status: !currentStatus } : user
        ));
      } catch (err) {
        console.error('Error updating user status:', err);
      }
    }
    
    setConfirmDialog({ open: false, userId: null, action: null, message: '' });
  };

  const handleImportCsv = async (event) => {
    try {
      const file = event.target.files[0];
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      await axios.post(USER_URLS.importUsers, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      window.location.reload();
    } catch (err) {
      console.error('Error importing CSV:', err);
    }
  };

  // Role cell component
  const RoleCell = ({ user }) => {
    const isSuperAdmin = currentUser?.role === "Super Admin";
    const isCurrentUser = user.id === currentUser?.id;
    
    if (!isSuperAdmin || isCurrentUser) {
      return <StyledTableCell>{user.role}</StyledTableCell>;
    }

    return (
      <StyledTableCell>
        <FormControl size="small" fullWidth>
          <Select
            value={user.role}
            onChange={(e) => handleRoleChange(user.id, e.target.value)}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="User">User</MenuItem>
            <MenuItem value="Admin">Admin</MenuItem>
            <MenuItem value="Super Admin">Super Admin</MenuItem>
          </Select>
        </FormControl>
      </StyledTableCell>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, color: 'error.main' }}>
        Error loading users: {error}
      </Box>
    );
  }

  const filteredUsers = getFilteredUsers();
  const paginatedUsers = filteredUsers.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Box sx={{ p: 3, bgcolor: '#FFFFFF' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          mb: 2
        }}>
          <Box>
            <Typography variant="h5" sx={{ 
              fontWeight: 500, 
              mb: 1,
              fontSize: '1.5rem'
            }}>
              User Management
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              fontSize: '0.875rem'
            }}>
              Add, invite and manage your organisation's members
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <input
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              id="csv-upload"
              onChange={handleImportCsv}
            />
            <label htmlFor="csv-upload">
              <StyledButton
                component="span"
                variant="outlined"
                startIcon={<FileUploadIcon />}
                sx={{ height: '36px' }}
              >
                Import CSV
              </StyledButton>
            </label>
            <StyledButton
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ height: '36px' }}
            >
              Add new user
            </StyledButton>
          </Box>
        </Box>
        <Box sx={{ 
          height: '2px', 
          bgcolor: '#F2F2F2', 
          width: '78%'
        }} />
      </Box>

      {/* Search and Filters */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        mb: 3,
        gap: 2
      }}>
        <TextField
          placeholder="Search by name, email or role"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ 
            width: '360px',
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#FFFFFF',
              '& fieldset': {
                borderColor: 'divider'
              }
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            )
          }}
        />

        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1
        }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
            Filter by
          </Typography>
          <FormControl size="small" sx={{ mr: 1 }}>
            <Select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              displayEmpty
              renderValue={roleFilter !== '' ? undefined : () => 'Role'}
              sx={{ 
                minWidth: '100px',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                }
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="User">User</MenuItem>
              <MenuItem value="Admin">Admin</MenuItem>
              <MenuItem value="Super_admin">Super Admin</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small">
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              displayEmpty
              renderValue={statusFilter !== '' ? undefined : () => 'Status'}
              sx={{ 
                minWidth: '100px',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                }
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>


      {/* Users Table */}
      <TableContainer component={Paper} elevation={0} sx={{ mb: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>NAME</StyledTableCell>
              <StyledTableCell>LAST ACTIVE</StyledTableCell>
              <StyledTableCell>EMAIL ADDRESS</StyledTableCell>
              <StyledTableCell>ORGANISATION</StyledTableCell>
              <StyledTableCell>
                ROLE
                <IconButton size="small" sx={{ ml: 0.5 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </StyledTableCell>
              <StyledTableCell>STATUS</StyledTableCell>
              <StyledTableCell align="right"></StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedUsers.map((user) => (
              <TableRow key={user.id} hover>
                <StyledTableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <StyledAvatar>
                      {user.first_name?.[0]}{user.last_name?.[0]}
                    </StyledAvatar>
                    <Typography>
                      {`${user.first_name} ${user.last_name}`}
                      {currentUser && user.id === currentUser.id && ' (you)'}
                    </Typography>
                  </Box>
                </StyledTableCell>
                <StyledTableCell sx={{ color: 'text.secondary' }}>
                  {formatLastActive(user.last_active)}
                </StyledTableCell>
                <StyledTableCell>{user.email}</StyledTableCell>
                <StyledTableCell>{user.organisation}</StyledTableCell>
                <RoleCell user={user} />
                <StyledTableCell>
                  <StyledSwitch 
                    checked={user.status}
                    onChange={() => handleStatusChange(user.id, user.status)}
                  />
                </StyledTableCell>
                <StyledTableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => handleActionsClick(e, user)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </StyledTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        px: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Rows per page:
          </Typography>
          <Select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(e.target.value);
              setPage(1);
            }}
            size="small"
            sx={{ 
              minWidth: 70,
              '& .MuiSelect-select': {
                py: 0.5
              }
            }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            {`${(page - 1) * rowsPerPage + 1}-${Math.min(page * rowsPerPage, filteredUsers.length)} of ${filteredUsers.length}`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            size="small" 
            onClick={() => setPage(prev => Math.max(1, prev - 1))}
            disabled={page === 1}
          >
            <NavigateBeforeIcon />
          </IconButton>
          <IconButton 
            size="small"
            onClick={() => setPage(prev => Math.min(Math.ceil(filteredUsers.length / rowsPerPage), prev + 1))}
            disabled={page >= Math.ceil(filteredUsers.length / rowsPerPage)}
          >
            <NavigateNextIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleActionsClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <MenuItem onClick={handleActionsClose}>Edit</MenuItem>
        <MenuItem onClick={handleActionsClose}>Delete</MenuItem>
      </Menu>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, userId: null, action: null, message: '' })}
      >
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmDialog({ open: false, userId: null, action: null, message: '' })}
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDialog} 
            color="primary" 
            variant="contained"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;